/**
 * Layout route for the /guide area: a slim topic rail (sticky left column on
 * desktop, a horizontally scrolling pill strip on mobile) beside the matched
 * child topic in an <Outlet />. Ported from the sister app's guide-shell.
 *
 * Active state: exact match for the Overview entry (/guide), prefix match
 * for every topic so deep links stay highlighted.
 */

import { BookOpen, type LucideIcon, TrendingUp, Truck } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router";

import { cn } from "@/lib/utils";

type GuideTopic = {
  href: string;
  label: string;
  icon: LucideIcon;
};

type GuideGroup = {
  label: string;
  topics: GuideTopic[];
};

const GROUPS: GuideGroup[] = [
  {
    label: "Guide",
    topics: [{ href: "/guide", label: "Overview", icon: BookOpen }],
  },
  {
    label: "Workflows",
    topics: [
      { href: "/guide/ordering", label: "Ordering comms", icon: Truck },
      { href: "/guide/share", label: "Order-share comms", icon: TrendingUp },
    ],
  },
];

export function GuideShell() {
  const pathname = useLocation().pathname;

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-10">
      <aside className="lg:sticky lg:top-8 lg:self-start">
        <nav className="flex flex-col gap-5">
          {GROUPS.map((group) => (
            <div key={group.label}>
              <p className="text-muted px-2 pb-2 text-[10px] font-medium tracking-[0.18em] uppercase">
                {group.label}
              </p>
              <ul className="flex flex-row gap-1 overflow-x-auto pb-1 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:pb-0">
                {group.topics.map((topic) => (
                  <TopicRow key={topic.href} topic={topic} pathname={pathname} />
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

function TopicRow({ topic, pathname }: { topic: GuideTopic; pathname: string }) {
  // Overview is exact-match (every topic path is a prefix of /guide); topics
  // use prefix-match so a nested deep link keeps the rail highlighted.
  const isActive =
    topic.href === "/guide"
      ? pathname === "/guide"
      : pathname === topic.href || pathname.startsWith(topic.href + "/");
  const Icon = topic.icon;

  return (
    <li className="shrink-0 lg:shrink">
      <Link
        to={topic.href}
        className={cn(
          "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors",
          isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent-light",
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">{topic.label}</span>
      </Link>
    </li>
  );
}
